<script setup lang="ts">
import { Icon } from '@iconify/vue';
import { onMounted, reactive, ref } from 'vue';
import { useRouter } from 'vue-router';
import { fetchStatsApi } from '@/api/stats/stats.ts';
import { fetchUserApi, loginApi } from '@/api/user/userApi';
import WechatQrPanel from '@/components/auth/WechatQrPanel.vue';
import { clearDynamicCourseContext } from '@/components/course/courseUtils';
import { useConfigStore, useUserStore } from '@/store';
import { setLocalToken, setRefreshToken } from '@/utils/auth';
import { error, success } from '@/utils/toast.ts';

const isStatsLoading = ref(true);
const stats = reactive({ user_count: 0, conversation_count: 0, comment_count: 0 });
const router = useRouter();
const userStore = useUserStore();
const isDevelopment = import.meta.env.DEV;
const isLocalAcceptance = import.meta.env.MODE === 'local-acceptance'
  || (import.meta.env.DEV
    && ['localhost', '127.0.0.1'].includes(window.location.hostname)
    && new URLSearchParams(window.location.search).get('localBackend') === '1');
const localUsername = ref('');
const localPassword = ref('');
const isLocalLoginLoading = ref(false);

function localTeacherPreviewLogin(event: SubmitEvent) {
  const form = event.currentTarget as HTMLFormElement;
  const formData = new FormData(form);
  const username = String(formData.get('username') || '').trim();
  const password = String(formData.get('password') || '');
  if (!username || !password) {
    error('请输入模拟教师账号和测试密码');
    return;
  }

  sessionStorage.setItem('probfun_local_teacher_preview', '1');
  userStore.user = {
    id: `local-preview-teacher-${username}`,
    username,
    classId: '',
    realName: username,
    gender: '',
    email: '',
    phone: '',
    school: '本地预览',
    major: '数学',
    grade: 0,
    source: 0,
    avatarUrl: '',
    role: 2,
    isSuperuser: false,
    isPlatformAdmin: false,
  };
  localPassword.value = '';
  success(`已进入 ${username} 的教师模拟账号`);
  router.replace('/courses/9001?returnTo=/my-courses');
}

async function localAcceptanceLogin(event: SubmitEvent) {
  if (!isLocalAcceptance)
    return;

  const form = event.currentTarget as HTMLFormElement;
  const formData = new FormData(form);
  const username = String(formData.get('username') || '').trim();
  const password = String(formData.get('password') || '');
  if (!username || !password) {
    error('请填写本地验收用户名和临时测试密码');
    return;
  }

  isLocalLoginLoading.value = true;
  try {
    const response = await loginApi(username, password);
    setLocalToken(response.token);
    setRefreshToken(response.refreshToken);
    sessionStorage.removeItem('probfun_local_teacher_preview');

    const profile = await fetchUserApi();
    userStore.user = profile.user;
    localPassword.value = '';
    success('本地验收账号登录成功');
    await router.replace(Number(profile.user.role) === 2 ? '/my-courses' : '/');
  }
  catch (exception: any) {
    error(exception?.message || '本地验收账号登录失败');
  }
  finally {
    isLocalLoginLoading.value = false;
  }
}

async function developmentLogin(event: SubmitEvent) {
  if (isLocalAcceptance)
    await localAcceptanceLogin(event);
  else
    localTeacherPreviewLogin(event);
}

// 北邮统一认证登录入口暂时隐藏，后续恢复时取消此处及模板中对应按钮的注释。
// function casLogin() {
//   const casServer = 'https://auth.bupt.edu.cn/authserver/login';
//   const serviceUrl = encodeURIComponent('https://umathhub.com/callback');
//   window.location.href = `${casServer}?service=${serviceUrl}`;
// }

onMounted(async () => {
  const configStore = useConfigStore();
  configStore.currentSubjectId = null;
  configStore.dynamicSubject = null;
  clearDynamicCourseContext();
  try {
    Object.assign(stats, await fetchStatsApi());
  }
  catch (exception) {
    console.error('Error fetching stats:', exception);
    error('获取统计信息失败');
  }
  finally {
    isStatsLoading.value = false;
  }
});
</script>

<template>
  <div class="auth-page relative isolate flex h-full w-full flex-col overflow-hidden">
    <header class="relative z-10 px-6 py-5 sm:px-10 lg:px-14">
      <div class="mx-auto flex max-w-7xl items-center">
        <div class="flex h-10 items-center gap-3">
          <img src="/logo-math.svg" alt="邮趣数学" class="size-11">
          <div class="text-xl font-bold tracking-tight text-primary sm:text-2xl">
            邮趣数学
          </div>
        </div>
        <div class="ml-auto hidden items-center gap-2 rounded-full border border-white/80 bg-white/75 px-4 py-2 text-xs font-medium text-slate-500 shadow-sm backdrop-blur sm:flex">
          <Icon icon="lucide:shield-check" class="size-4 text-emerald-500" />
          安全登录 · 开启数学之旅
        </div>
      </div>
    </header>

    <main class="relative z-10 flex flex-1 overflow-auto px-4 pb-6 sm:px-8 lg:items-center lg:pb-10">
      <div class="auth-shell mx-auto grid w-full max-w-xl overflow-hidden rounded-[28px] border border-white/90 bg-white/90 shadow-2xl shadow-slate-300/40 backdrop-blur-xl">
        <!-- 左侧登录说明、统计及其他登录入口暂时隐藏；恢复时移除 v-if 并还原外层双栏和 max-w-6xl 样式。 -->
        <section v-if="false" class="flex flex-col p-7 sm:p-9 lg:p-11">
          <div class="mb-7 inline-flex w-fit items-center gap-2 rounded-full bg-blue-50 px-3 py-1.5 text-xs font-semibold text-blue-600">
            <Icon icon="mdi:wechat" class="size-3.5 text-[#07c160]" />
            微信扫码登录
          </div>
          <h1 class="text-3xl font-bold tracking-tight text-slate-950 sm:text-4xl">
            登录邮趣数学
          </h1>
          <p class="mt-3 max-w-xl text-sm leading-6 text-slate-500 sm:text-base">
            使用微信扫码进入学习空间。北邮统一认证可在登录后的用户栏中完成绑定。
          </p>

          <div class="my-7 text-sm text-neutral-600">
            <div v-if="isStatsLoading" class="text-sm text-neutral-500">
              加载统计中...
            </div>
            <div v-else class="grid grid-cols-3 overflow-hidden rounded-2xl border border-slate-100 bg-slate-50/80 py-4">
              <div class="flex flex-col items-center gap-1 border-r border-slate-200 px-2 text-center">
                <div class="text-lg font-bold text-primary sm:text-xl">
                  {{ stats.user_count }}
                </div>
                <div class="text-xs text-slate-500">
                  服务用户
                </div>
              </div>
              <div class="flex flex-col items-center gap-1 border-r border-slate-200 px-2 text-center">
                <div class="text-lg font-bold text-primary sm:text-xl">
                  {{ stats.conversation_count }}
                </div>
                <div class="text-xs text-slate-500">
                  AI 会话
                </div>
              </div>
              <div class="flex flex-col items-center gap-1 px-2 text-center">
                <div class="text-lg font-bold text-primary sm:text-xl">
                  {{ stats.comment_count }}
                </div>
                <div class="text-xs text-slate-500">
                  用户评论
                </div>
              </div>
            </div>
          </div>

          <div class="rounded-2xl border border-blue-100 bg-blue-50/70 p-4 text-sm leading-6 text-slate-600">
            <div class="flex items-center gap-2 font-semibold text-slate-800">
              <Icon icon="lucide:info" class="size-4 text-primary" />
              北邮统一认证改为登录后绑定
            </div>
            <p class="mt-1 text-xs text-slate-500">
              绑定后会关联你的学工号、课程身份和校内学习权益。
            </p>
          </div>

          <!-- 北邮统一认证登录入口暂时隐藏，后续需要时可恢复。
          <Button class="mt-6 h-14 w-full rounded-xl text-base font-semibold shadow-lg shadow-blue-200 transition-all hover:-translate-y-0.5 hover:shadow-xl" @click="casLogin">
            <Icon icon="lucide:key-round" class="mr-2 size-5" />
            使用北邮统一认证登录
            <Icon icon="lucide:arrow-right" class="ml-2 size-4" />
          </Button>
          -->

          <div class="mt-5 flex items-center gap-2 text-xs text-slate-500">
            <Icon icon="lucide:circle-help" class="size-3.5" />
            登录遇到问题？请添加 QQ 群
            <span class="font-medium text-slate-700 underline underline-offset-4">111293253</span>
          </div>
        </section>

        <aside class="bg-slate-50/60 p-6 sm:p-8 lg:p-10">
          <form v-if="isDevelopment" class="space-y-4" @submit.prevent="developmentLogin">
            <div class="text-center">
              <h1 class="text-xl font-semibold text-slate-900">
                {{ isLocalAcceptance ? '本地课程权限验收' : '教师模拟登录' }}
              </h1>
              <p class="mt-2 text-sm text-slate-500">
                {{ isLocalAcceptance ? '连接验收环境并验证真实测试账号' : '仅用于本地页面预览，账号密码不会发送到服务器' }}
              </p>
            </div>
            <label class="block text-sm font-medium text-slate-700">
              {{ isLocalAcceptance ? '本地验收用户名' : '教师账号' }}
              <input v-model="localUsername" name="username" autocomplete="username" :placeholder="isLocalAcceptance ? '' : '例如：李巧悦'" class="mt-1 w-full rounded-xl border border-slate-200 bg-white px-4 py-3 outline-none focus:border-primary" required>
            </label>
            <label class="block text-sm font-medium text-slate-700">
              {{ isLocalAcceptance ? '临时测试密码' : '测试密码' }}
              <input v-model="localPassword" name="password" type="password" :autocomplete="isLocalAcceptance ? 'current-password' : 'off'" :placeholder="isLocalAcceptance ? '' : '请输入任意测试密码（请勿使用真实密码）'" class="mt-1 w-full rounded-xl border border-slate-200 bg-white px-4 py-3 outline-none focus:border-primary" required>
            </label>
            <button type="submit" class="w-full rounded-xl bg-primary px-4 py-3 font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60" :disabled="isLocalLoginLoading">
              {{ isLocalLoginLoading ? '正在登录…' : isLocalAcceptance ? '登录本地验收环境' : '进入教师端' }}
            </button>
          </form>
          <WechatQrPanel v-else mode="login" class="wechat-panel" />
          <div class="mt-5 flex items-center justify-center gap-2 text-xs text-slate-500">
            <Icon icon="lucide:circle-help" class="size-3.5" />
            登录遇到问题？请添加 QQ 群
            <span class="font-medium text-slate-700 underline underline-offset-4">111293253</span>
          </div>
        </aside>
      </div>
    </main>
  </div>
</template>

<style scoped>
.auth-page { background: #f8fafc; }
.auth-page::before { position: absolute; z-index: -2; inset: 0; background: url('/img_2.png') center bottom / cover no-repeat; content: ''; filter: blur(0.5px) saturate(1); opacity: 0.78; transform: scale(1.015); }
.auth-page::after { position: absolute; z-index: -1; inset: 0; background: radial-gradient(circle at 72% 18%, rgba(59, 130, 246, 0.08), transparent 26rem), linear-gradient(180deg, rgba(255, 255, 255, 0.86) 0%, rgba(247, 250, 253, 0.58) 48%, rgba(240, 247, 250, 0.16) 100%); content: ''; }
.auth-shell { min-height: min(700px, calc(100vh - 128px)); }
.wechat-panel { height: auto; border-color: transparent; background: transparent; box-shadow: none; }
@media (max-width: 1023px) { .auth-shell { min-height: auto; } }
</style>
